import { getChartTitle } from '@/shared/copy/charts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s } from '@/theme/tokens';
import { StatusBanner } from '@/ui';
import { ChartEmptyState } from '@/ui/composites';
import { Panel } from '@/ui/composites/Panel';
import { Box, PanelHeadingText, PieChart } from '@/ui/primitives';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import { type StackedMetricsMiniPanelsLayout } from './StackedMetricsStatusChartSection.layout';
import { renderStatusPieLoadingState } from './StackedMetricsStatusChartSection.states';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type { ReactElement } from 'react';

type StatusPieBreakdownPanelProps = {
  loading: boolean;
  showPlaceholder?: boolean;
  showErrorState: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  miniPanels: StackedMetricsMiniPanelsLayout;
  headerToChartGap: number;
  miniMetricEmptyTitle: string;
  statusPieData: StatusPieDatum[];
  statusLegend: ReactElement | null;
  legendMarginTop?: string;
};

export const StatusPieBreakdownPanel = ({
  loading,
  showPlaceholder = false,
  showErrorState,
  piePanelHeight,
  pieChartVisualHeight,
  miniPanels,
  headerToChartGap,
  miniMetricEmptyTitle,
  statusPieData,
  statusLegend,
  legendMarginTop = `-${s('xs')}`,
}: StatusPieBreakdownPanelProps): ReactElement => {
  return (
    <Panel
      padding="none"
      gap="none"
      contentGap="none"
      sx={{
        minWidth: 0,
        height: piePanelHeight,
        backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: s('xl'),
            pt: s('lg'),
            pb: miniPanels.headerPaddingBottom,
            minHeight: `${miniPanels.headerHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <PanelHeadingText
            component="h2"
            sx={{
              minWidth: 0,
              textAlign: 'center',
            }}
          >
            {getChartTitle('statusBreakdown')}
          </PanelHeadingText>
        </Box>
        <Box
          sx={{
            px: s('lg'),
            pb: s('lg'),
            pt: s('sm'),
            flex: 1,
            minHeight: 0,
            display: 'grid',
            alignItems: 'stretch',
            boxSizing: 'border-box',
          }}
        >
          {showPlaceholder ? (
            <Box
              aria-hidden="true"
              sx={{
                width: '100%',
                height: pieChartVisualHeight,
                minHeight: pieChartVisualHeight,
              }}
            />
          ) : loading ? (
            renderStatusPieLoadingState(pieChartVisualHeight)
          ) : showErrorState ? (
            <Box
              sx={{
                width: '100%',
                height: pieChartVisualHeight,
                minHeight: pieChartVisualHeight,
                display: 'grid',
                placeItems: 'center',
                transform: `translateY(${headerToChartGap}px)`,
              }}
            >
              <StatusBanner
                tone="red"
                variant="outlined"
                message={getErrorCopy('banner.chartLoadFailed')}
                sx={chartLoadErrorBannerSx}
              />
            </Box>
          ) : statusPieData.length > 0 ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: 0,
                transform: `translateY(-${s('lg')})`,
              }}
            >
              <PieChart
                data={statusPieData}
                height={pieChartVisualHeight}
                innerRadius={38}
                outerRadius="84%"
                showLegend={false}
              />
              <Box sx={{ mt: legendMarginTop }}>{statusLegend}</Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: pieChartVisualHeight,
                minHeight: pieChartVisualHeight,
                display: 'grid',
                placeItems: 'center',
                transform: `translateY(${headerToChartGap}px)`,
              }}
            >
              <ChartEmptyState title={miniMetricEmptyTitle} />
            </Box>
          )}
        </Box>
      </Box>
    </Panel>
  );
};
